import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalanceJudgeMeRuntime";
const JUDGE_ME_SCRIPT_SELECTOR = 'script[src*="judge.me"], script[src*="judgeme"]';

interface JudgeMeRuntime {
  cleanup: () => void;
}

interface JudgeMeRuntimeWindow extends Window {
  [RUNTIME_KEY]?: JudgeMeRuntime;
}

function callIfFunction(candidate: unknown): boolean {
  if (typeof candidate !== "function") {
    return false;
  }

  try {
    (candidate as () => void)();
    return true;
  } catch {
    return false;
  }
}

function dedupeJudgeMeScripts(): number {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>(JUDGE_ME_SCRIPT_SELECTOR));
  if (scripts.length <= 1) {
    return scripts.length;
  }

  scripts.slice(1).forEach((duplicateScript) => {
    duplicateScript.parentNode?.removeChild(duplicateScript);
  });
  return 1;
}

function refreshJudgeMe(reason: string): boolean {
  const invoked =
    callIfFunction(window.jdgm?.refreshBadges) ||
    callIfFunction(window.jdgm?.renderBadges) ||
    callIfFunction(window.jdgm?.init) ||
    callIfFunction(window.jdgm?.allWidgetsRating);
  const scriptCount = dedupeJudgeMeScripts();

  document.documentElement.setAttribute("data-build-judge-me-adapter", "ready");
  document.documentElement.setAttribute(
    "data-build-judge-me-last-refresh",
    `${reason}:${invoked ? "invoked" : "no-op"}`
  );
  document.documentElement.setAttribute("data-build-judge-me-script-count", String(scriptCount));
  window.dispatchEvent(
    new CustomEvent("rebalance:judge-me:refresh", {
      detail: { reason, invoked, scriptCount }
    })
  );

  return invoked;
}

export function initJudgeMe(): void {
  const runtimeWindow = window as JudgeMeRuntimeWindow;
  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let refreshTimer = 0;

  const scheduleRefresh = (reason: string, delayMs = 180): void => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshTimer = 0;
      refreshJudgeMe(reason);
    }, delayMs);
  };

  const handleGridUpdated = (): void => scheduleRefresh("collection-grid-updated");
  const handleRecommendationsUpdated = (): void => scheduleRefresh("recommendations-updated");
  const handleAfterAdd = (): void => scheduleRefresh("cart-after-add");
  const unlistenRouteInitialized = themeEvents.on("route:initialized", ({ template }) => {
    scheduleRefresh(`route-initialized:${template}`);
  });

  document.addEventListener("baseline:collection:grid-updated", handleGridUpdated);
  document.addEventListener("baseline:product:recommendations-updated", handleRecommendationsUpdated);
  document.body.addEventListener("baseline:modalcart:afteradditem", handleAfterAdd);

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      document.removeEventListener("baseline:collection:grid-updated", handleGridUpdated);
      document.removeEventListener("baseline:product:recommendations-updated", handleRecommendationsUpdated);
      document.body.removeEventListener("baseline:modalcart:afteradditem", handleAfterAdd);
      unlistenRouteInitialized();
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = 0;
      }
    }
  };

  scheduleRefresh("adapter-init", 0);
}
